import { useTheme } from '@ihs-widget/stores/useTheme';
import { useEffect, useState, useRef, useContext, useMemo, useCallback } from 'react';
import FilterButton from '@ihs-widget/components/StatsConsole/Widgets/common/FilterButton';
import { type IState, StateContext } from "../../../../../state/stateContext";
import { T, H2HActiveFilterName } from "@ihs-widget/utils/enums";
import { useLanguage } from '@ihs-widget/utils/hooks/useLanguage';
import { getIsMobileDevice } from '@ihs-widget/utils/functions';
import { LeftArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/LeftArrowInChipsFilters';
import { RightArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/RightArrowInChipsFilters';
import { useTranslations } from '../../../../../translation/TranslationContext';

interface FiltersH2HProps {
    h2hData: any;
    menuItemsH2HContainerRef: React.RefObject<HTMLDivElement | null>;
}

const FiltersH2H = ({ h2hData, menuItemsH2HContainerRef }: FiltersH2HProps) => {
    const { theme } = useTheme();

    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { h2hTabActiveFilter } = stContext || {};

    const language = useLanguage();
    const t = useTranslations(language);

    const menuItemsH2HRef = useRef<HTMLDivElement>(null);
    const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
    const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const getAvailableFilters = () => {
        if (!h2hData?.data?.match_statistics || !Array.isArray(h2hData.data.match_statistics)) {
            return {};
        }

        const filters: { [key: string]: string } = {};
        
        h2hData.data.match_statistics.forEach((item: any) => {
            if (item.type && item.label && item.data) {
                let hasData = false;

                switch (item.type) {
                    case 'h2h':
                        hasData = !!(item.data.score || item.data.latest_matches_home?.length > 0 || item.data.latest_matches_away?.length > 0);
                        break;
                    case 'table':
                        hasData = !!(item.data.tableRows?.length > 0);
                        break;
                    case 'knockout':
                        hasData = !!(item.data.matches?.length > 0 || item.data.stages?.length > 0);
                        break;
                    case 'qualification':
                        hasData = !!(item.data.stages?.length > 0);
                        break;
                    case 'groups_tables':
                        hasData = !!(item.data.groups?.length > 0);
                        break;
                    default:
                        hasData = item.data && Object.keys(item.data).length > 0;
                }
                
                if (!hasData) {
                    return;
                }
                
                switch (item.type) {
                    case 'h2h':
                        filters.H2H = t[item.label as keyof typeof t] || 'H2H';
                        break;
                    case 'table':
                        filters.TABLE = t[item.label as keyof typeof t] || 'Table';
                        break;
                    case 'knockout':
                        if (item.label === 'InHouseStats.QuarterFinals') {
                            filters.QUARTER_FINALS = t[item.label as keyof typeof t] || 'Quarter Finals';
                        } else if (item.label === 'InHouseStats.RoundOf16') {
                            filters.ROUND_OF_16 = t[item.label as keyof typeof t] || 'Round of 16';
                        } else if (item.label === 'InHouseStats.Playoffs') {
                            filters.KNOCKOUT = t[item.label as keyof typeof t] || 'Playoffs';
                        } else if (item.label === 'InHouseStats.QualifyingRound') {
                            filters.QUALIFICATION = t[item.label as keyof typeof t] || 'Qualification';
                        } else {
                            filters.KNOCKOUT = t[item.label as keyof typeof t] || 'Knockout';
                        }
                        break;
                    case 'qualification':
                        filters.QUALIFICATION = t[item.label as keyof typeof t] || 'Qualification';
                        break;
                    case 'groups_tables':
                        filters.GROUPS_TABLES = t[item.label as keyof typeof t] || 'Groups Tables';
                        break;
                }
            }
        });

        return filters;
    };

    const SCH2HActiveFilter = useMemo(() => getAvailableFilters(), [h2hData, t]);
    type SCH2HActiveFilterKey = keyof typeof SCH2HActiveFilter;

    const setActiveFilter = useCallback((filter: SCH2HActiveFilterKey | null) => {
        setStContext((prev: IState) => ({
            ...prev,
            h2hTabActiveFilter: filter,
            h2hTabActiveFilterName: getActiveFilterName(filter)
        }));
    }, [setStContext]);

    const getActiveFilterName = (filter: SCH2HActiveFilterKey | null): H2HActiveFilterName => {
        switch (filter) {
            case 'H2H':
                return H2HActiveFilterName.H2H;
            case 'TABLE':
                return H2HActiveFilterName.TABLE;
            case 'QUARTER_FINALS':
                return H2HActiveFilterName.QUARTER_FINALS;
            case 'ROUND_OF_16':
                return H2HActiveFilterName.ROUND_OF_16;
            case 'KNOCKOUT':
                return H2HActiveFilterName.KNOCKOUT;
            case 'QUALIFICATION':
                return H2HActiveFilterName.QUALIFICATION;
            case 'GROUPS_TABLES':
                return H2HActiveFilterName.GROUPS_TABLES;
            default:
                return H2HActiveFilterName.H2H;
        }
    };

    const onClickLeftArrow = () => {
        const menuItemsContainer = menuItemsH2HRef.current;
        if (menuItemsContainer) {
            const currentScroll = menuItemsContainer.scrollLeft;
            const scrollAmount = Math.min(40, currentScroll);
            menuItemsContainer.scrollLeft -= scrollAmount;
        }
    }

    const onClickRightArrow = () => {
        const menuItemsContainer = menuItemsH2HRef.current;
        if (menuItemsContainer) {
            const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
            const currentScroll = menuItemsContainer.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const scrollAmount = Math.min(40, remainingScroll);
            menuItemsContainer.scrollLeft += scrollAmount;
        }
    }

    useEffect(() => {
        if (h2hTabActiveFilter) {
            setStContext((prev: IState) => ({
                ...prev,
                h2hTabActiveFilterName: getActiveFilterName(h2hTabActiveFilter)
            }));
        }
    }, [h2hTabActiveFilter, setStContext]);

    useEffect(() => {
        const availableFilters = Object.keys(SCH2HActiveFilter);
        
        if (availableFilters.length === 0) {
            return;
        }

        if (!h2hTabActiveFilter) {
            const priorityOrder = ['H2H', 'TABLE', 'QUARTER_FINALS', 'ROUND_OF_16', 'KNOCKOUT', 'QUALIFICATION', 'GROUPS_TABLES'];
            const firstAvailable = priorityOrder.find(filter => availableFilters.includes(filter)) || availableFilters[0];
            setActiveFilter(firstAvailable as SCH2HActiveFilterKey);
        } else {
            const currentFilterExists = availableFilters.includes(h2hTabActiveFilter);
            if (!currentFilterExists) {
                const priorityOrder = ['H2H', 'TABLE', 'QUARTER_FINALS', 'ROUND_OF_16', 'KNOCKOUT', 'QUALIFICATION', 'GROUPS_TABLES'];
                const firstAvailable = priorityOrder.find(filter => availableFilters.includes(filter)) || availableFilters[0];
                setActiveFilter(firstAvailable as SCH2HActiveFilterKey);
            }
        }
    }, [SCH2HActiveFilter, h2hTabActiveFilter, setActiveFilter]);

    useEffect(() => {
        const isMobileDevice = getIsMobileDevice();
        if (isMobileDevice) {
            setIsMenuArrowsVisible(false);
        } else {
            if (isMenuItemsScrollable) {
                setIsMenuArrowsVisible(true);
            } else {
                setIsMenuArrowsVisible(false);
            }
        }
    }, [isMenuItemsScrollable])

    useEffect(() => {
        const menuItemsContainer = menuItemsH2HRef.current;
        if (menuItemsContainer) {
            const handleScroll = () => {
                const currentScroll = menuItemsContainer.scrollLeft;
                const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
                setIsLeftArrowDisabled(currentScroll === 0);
                setIsRightArrowDisabled(Math.abs(currentScroll - maxScroll) < 1);
            };
            
            menuItemsContainer.addEventListener('scroll', handleScroll);
            handleScroll();
            
            return () => {
                menuItemsContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [window.innerWidth]);

    useEffect(() => {
        const checkScrollability = () => {
            if (menuItemsH2HContainerRef?.current && menuItemsH2HRef?.current) {
                const itemsWidth = menuItemsH2HRef.current.scrollWidth;
                const containerWidth = menuItemsH2HRef.current.clientWidth;
                setIsMenuItemsScrollable(itemsWidth > containerWidth);
            } else {
                setIsMenuItemsScrollable(false);
            }
        };

        checkScrollability();
        window.addEventListener('resize', checkScrollability);

        return () => {
            window.removeEventListener('resize', checkScrollability);
        };
    }, [language, h2hData, menuItemsH2HContainerRef]);

    if (Object.keys(SCH2HActiveFilter).length === 0) {
        return null;
    }

    return (
        <div className='flex flex-row justify-center items-center gap-[3px] w-full select-none'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            {isMenuArrowsVisible &&
                <LeftArrowInChipsFilters
                    color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#111111' : '#00531D') : (isLeftArrowDisabled ? '#111111' : '#00531D')}
                    onClick={onClickLeftArrow}
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isLeftArrowDisabled}
                />
            }
            <div ref={menuItemsH2HRef}
                className={`pl-[8px] flex min-w-full items-center mt-[8px] mb-[12px]
                                        overflow-x-scroll menuItemsStats-disable-scrollbar z-10 
                                        [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                                        ${isMenuArrowsVisible && 'justify-start'}
                                        ${!isMenuArrowsVisible && isMenuItemsScrollable && 'justify-start'}
                                        ${!isMenuArrowsVisible && !isMenuItemsScrollable && 'justify-center'}`}>
                {Object.entries(SCH2HActiveFilter).map(([key, value], index) => (
                    <FilterButton
                        key={key}
                        text={value}
                        isActive={h2hTabActiveFilter === key}
                        onClick={() => setActiveFilter(key as SCH2HActiveFilterKey)}
                        additionalClassName={`${index < Object.keys(SCH2HActiveFilter).length - 1 ? 'mr-[8px]' : ''}`}
                        id={`filter-${key.toLowerCase()}`}
                    />
                ))}
            </div>
            {isMenuArrowsVisible &&
                <RightArrowInChipsFilters
                    color={theme === T.LIGHT ? (isRightArrowDisabled ? '#111111' : '#00531D') : (isRightArrowDisabled ? '#111111' : '#00531D')}
                    onClick={onClickRightArrow}
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isRightArrowDisabled}
                />
            }
        </div>
    )
}

export default FiltersH2H;
